import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { theme } from '../../styles/GlobalStyle';

/*
 * Dupla hélice em 3D de verdade, só com CSS (09/10, pedido do Valdair: "o DNA em 3D, bem feito").
 *
 * Como funciona:
 * - Cada conta percorre o próprio círculo em volta do eixo vertical (translate3d em x e z) com
 *   perspectiva no palco: a de trás fica menor e mais clara, a da frente maior e mais forte.
 *   A conta nunca gira em si, então continua redonda em qualquer ângulo.
 * - Todas usam a MESMA animação; a fase de cada uma é um animation-delay negativo. Os pares de
 *   base giram junto (rotateY) e ligam a conta da fita A à da fita B no mesmo andar.
 * - Hélice destra, como o B-DNA: 10,5 pares por volta, e as fitas a 150° uma da outra (não 180°),
 *   o que desenha o sulco maior e o menor. Girando no sentido horário visto de cima, o desenho sobe.
 * - Só transform e opacity animam: tudo roda no compositor do navegador, sem recalcular estilo nem
 *   layout na thread principal (medido: 0 recálculos em 3 s). O giro segue liso mesmo com a ficha
 *   ocupando a CPU enquanto carrega.
 * - prefers-reduced-motion: a hélice fica parada e o conjunto só "respira" (opacidade).
 *
 * A geometria é em em: `tamanho` muda a fonte do palco e tudo escala junto, sem perder nitidez.
 */

const PARES = 15;                 // pares de base visíveis
const CONTAS_POR_PAR = 3;         // 1 conta no par + 2 entre um par e outro: a fita parece contínua
const PASSO = 0.86;               // distância vertical entre pares (em)
const RAIO = 2.4;                 // raio da hélice (em)
const GIRO_POR_PAR = 360 / 10.5;  // B-DNA: 10,5 pares por volta
const ENTRE_FITAS = 150;          // separação angular das fitas (graus) → sulco maior e menor
const PERSPECTIVA = 32;           // em
const VOLTA_S = 5;                // segundos por volta
const MARGEM = 1;                 // folga acima e abaixo (em)
const CONTA = 1.6;                // a conta é desenhada grande e reduzida no transform (fica nítida)
const ESCALA_FRENTE = 0.4;
const ESCALA_FUNDO = 0.22;
const OPACIDADE_FUNDO = 0.28;
const QUADROS = 36;               // 1 quadro a cada 10°: o polígono da órbita é indistinguível do círculo

const ALTURA = (PARES - 1) * PASSO + 2 * MARGEM;   // ≈ 14 em
const LARGURA = 2 * RAIO + 2 * MARGEM;
const rad = (g: number) => (g * Math.PI) / 180;
const n = (v: number) => +v.toFixed(3);

// Órbita de uma conta que começa na frente (θ = 0) e gira no sentido horário visto de cima.
const quadrosDaOrbita = Array.from({ length: QUADROS + 1 }, (_, k) => {
  const p = k / QUADROS;
  const th = -2 * Math.PI * p;
  const frente = (1 + Math.cos(th)) / 2;                       // 1 na frente, 0 no fundo
  const escala = ESCALA_FUNDO + (ESCALA_FRENTE - ESCALA_FUNDO) * frente;
  const opacidade = OPACIDADE_FUNDO + (1 - OPACIDADE_FUNDO) * Math.pow(frente, 0.6);
  return `${n(p * 100)}% { transform: translate3d(${n(RAIO * Math.sin(th))}em, 0, ${n(RAIO * Math.cos(th))}em) scale(${n(escala)}); opacity: ${n(opacidade)}; }`;
}).join('\n');

// Par de base: a corda que liga a conta da fita A (θ) à da fita B (θ + 150°), no mesmo andar.
const RECUO_DA_CORDA = RAIO * Math.cos(rad(ENTRE_FITAS / 2));
const quadrosDoPar = Array.from({ length: QUADROS + 1 }, (_, k) => {
  const p = k / QUADROS;
  const th = -360 * p;
  const meioNaFrente = (1 + Math.cos(rad(th + ENTRE_FITAS / 2))) / 2;
  const opacidade = 0.5 * (0.45 + 0.55 * Math.pow(meioNaFrente, 0.7));
  return `${n(p * 100)}% { transform: rotateY(${n(ENTRE_FITAS / 2 + 180 + th)}deg) translateZ(${n(-RECUO_DA_CORDA)}em); opacity: ${n(opacidade)}; }`;
}).join('\n');

const orbita = keyframes`${quadrosDaOrbita}`;
const giroDoPar = keyframes`${quadrosDoPar}`;

const respiro = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;

const surgir = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Fase inicial (s) de quem está no ângulo θ: delay negativo põe cada peça no seu ponto da volta.
const fase = (thGraus: number) => {
  const p = (((-thGraus / 360) % 1) + 1) % 1;
  return `${n(-p * VOLTA_S)}s`;
};

type Peca = { chave: string; tipo: 'a' | 'b' | 'par'; menor: boolean; topo: number; atraso: string };

const PECAS: Peca[] = (() => {
  const lista: Peca[] = [];
  const total = (PARES - 1) * CONTAS_POR_PAR;
  for (let u = 0; u <= total; u++) {
    const andar = u / CONTAS_POR_PAR;
    const thA = -andar * GIRO_POR_PAR;                // subir na hélice = girar anti-horário (destra)
    const topo = MARGEM + andar * PASSO;
    const noPar = u % CONTAS_POR_PAR === 0;
    lista.push({ chave: `a${u}`, tipo: 'a', menor: !noPar, topo, atraso: fase(thA) });
    lista.push({ chave: `b${u}`, tipo: 'b', menor: !noPar, topo, atraso: fase(thA + ENTRE_FITAS) });
    if (noPar) lista.push({ chave: `p${u}`, tipo: 'par', menor: false, topo, atraso: fase(thA) });
  }
  return lista;
})();

const Palco = styled.div`
  position: relative;
  width: ${n(LARGURA)}em;
  height: ${n(ALTURA)}em;
  perspective: ${PERSPECTIVA}em;
  /* as pontas se dissolvem: a molécula continua além do que se vê */
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 14%, #000 86%, transparent 100%);
  mask-image: linear-gradient(to bottom, transparent 0, #000 14%, #000 86%, transparent 100%);

  @media (prefers-reduced-motion: reduce) {
    animation: ${respiro} 2.6s ease-in-out infinite;
  }
`;

const Helice = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform-style: preserve-3d;
`;

// as contas entre um par e outro são um pouco menores: a fita afina e engrossa como um colar
const lado = (menor: boolean) => n(menor ? CONTA * 0.85 : CONTA);

const Conta = styled.span<{ $fita: 'a' | 'b'; $menor: boolean }>`
  position: absolute;
  left: 50%;
  width: ${p => lado(p.$menor)}em;
  height: ${p => lado(p.$menor)}em;
  margin: ${p => n(-lado(p.$menor) / 2)}em 0 0 ${p => n(-lado(p.$menor) / 2)}em;
  border-radius: 50%;
  /* esfera fosca, luz suave do alto à esquerda: fita A terracota (conduz), fita B no cinza da marca, um tom acima */
  background: ${p => (p.$fita === 'a'
    ? 'radial-gradient(circle at 36% 32%, #C1876A 0%, #9E5F45 45%, #864C35 100%)'
    : 'radial-gradient(circle at 36% 32%, #D2D0CD 0%, #ADABA7 45%, #95938F 100%)')};
  animation: ${orbita} ${VOLTA_S}s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-play-state: paused;
  }
`;

const LARGURA_DO_PAR = 2 * RAIO * Math.sin(rad(ENTRE_FITAS / 2)) - 0.5;

const Par = styled.span`
  position: absolute;
  left: 50%;
  width: ${n(LARGURA_DO_PAR)}em;
  height: 0.12em;
  margin: -0.06em 0 0 ${n(-LARGURA_DO_PAR / 2)}em;
  border-radius: 0.06em;
  /* metade de cada base: a ponta direita encosta na fita A (terracota), a esquerda na B (cinza) */
  background: linear-gradient(90deg, #B9B7B3 0 50%, #A86A50 50% 100%);
  animation: ${giroDoPar} ${VOLTA_S}s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-play-state: paused;
  }
`;

interface HeliceDnaProps {
  /** Altura da hélice em px (padrão 140). */
  tamanho?: number;
  className?: string;
}

/** Só a hélice (decorativa). Para tela de espera com texto, use o CarregandoDna. */
export const HeliceDna = React.memo(function HeliceDna({ tamanho = 140, className }: HeliceDnaProps) {
  return (
    <Palco className={className} style={{ fontSize: `${n(tamanho / ALTURA)}px` }} aria-hidden="true">
      <Helice>
        {PECAS.map(p => (p.tipo === 'par'
          ? <Par key={p.chave} style={{ top: `${n(p.topo)}em`, animationDelay: p.atraso }} />
          : <Conta key={p.chave} $fita={p.tipo} $menor={p.menor} style={{ top: `${n(p.topo)}em`, animationDelay: p.atraso }} />
        ))}
      </Helice>
    </Palco>
  );
});

const Tela = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  /* espera curta não pisca: só aparece se passar de ~120 ms */
  animation: ${surgir} 0.45s ease-out 0.12s both;
`;

const Titulo = styled.div`
  margin-top: 22px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.28em;
  /* o espaçamento entra depois da última letra; o recuo igual à esquerda devolve o centro óptico */
  padding-left: 0.28em;
  text-transform: uppercase;
  color: ${theme.colors.primary};
`;

const Mensagem = styled.div`
  margin-top: 6px;
  max-width: 280px;
  font-size: 13px;
  line-height: 1.45;
  color: ${theme.colors.textMuted};
`;

const SoParaLeitor = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

interface CarregandoDnaProps {
  /** Linha de cima (ex.: "Essentia"). */
  titulo?: string;
  /** Linha de baixo (ex.: "Preparando inteligência clínica…"). */
  mensagem?: string;
  /** Altura da hélice em px (padrão 140). */
  tamanho?: number;
}

/**
 * Tela de espera com a hélice, título e mensagem centralizados um sobre o outro.
 * Para ficar no centro exato da área útil do admin, use dentro de `<AdminLayout centralizado>`.
 */
const CarregandoDna: React.FC<CarregandoDnaProps> = ({ titulo, mensagem, tamanho }) => {
  const { t } = useTranslation();
  return (
    <Tela role="status" aria-live="polite">
      <HeliceDna tamanho={tamanho} />
      {titulo && <Titulo>{titulo}</Titulo>}
      {mensagem && <Mensagem>{mensagem}</Mensagem>}
      {!titulo && !mensagem && <SoParaLeitor>{t('common.loading')}</SoParaLeitor>}
    </Tela>
  );
};

export default CarregandoDna;
